import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@firebird-web/shared-config';
import { LocalStorageService } from '@firebird-web/shared-services';
import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable } from 'rxjs';

declare let pendo: any;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authCodeFlowConfig: AuthConfig = {
    issuer: environment.identityServer,
    tokenEndpoint: environment.identityServer + '/oauth/token',
    redirectUri: window.location.origin + '/auth/callback/signin',
    clientId: environment.clientid,
    responseType: 'code',
    scope: 'openid profile offline_access ag2-trader-app',
    showDebugInformation: true,
    postLogoutRedirectUri: window.location.origin + '/auth/callback/logout',
  };

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private user: string;
  private sub: string;
  private errorHandlerSetup = false;

  private init$ = new BehaviorSubject<boolean>(false);

  constructor(
    private oauthService: OAuthService,
    private readonly localStorageService: LocalStorageService,
    private readonly httpClient: HttpClient
  ) {}

  public initialize(): Promise<void> {
    this.oauthService.configure(this.authCodeFlowConfig);
    this.oauthService.setStorage(localStorage);

    return new Promise((resolve, reject) => {
      // Set up error handling before attempting login
      this.setupErrorHandling();

      this.oauthService
        .loadDiscoveryDocumentAndTryLogin()
        .then(() => {
          if (!this.oauthService.hasValidAccessToken()) {
            // Before redirecting, emit initialization and resolve the Promise
            this.init$.next(true);
            resolve();

            const state = window.location.pathname + window.location.search;
            this.oauthService.initCodeFlow(state); // Triggers redirect to login
            return;
          }

          // Logged in with valid token, load user info and complete init
          this.oauthService.loadUserProfile().then((data: any) => {
            this.user = data.info.given_name + ' ' + data.info.family_name;
            this.sub = data.info.sub;
            this.init$.next(true);
            this.initializePendo(data.info.sub);
            resolve();
          });
        })
        .catch((error) => {
          console.error('OAuthService initialization error:', error);

          // Check if this is a recoverable auth error
          if (this.isRecoverableAuthError(error)) {
            console.log(
              'Recoverable auth error detected, clearing state and retrying...'
            );
            this.clearAuthStateAndRetry(resolve, reject);
          } else {
            reject(error); // Fail the promise for non-recoverable errors
          }
        });

      // Enable silent refresh
      this.oauthService.setupAutomaticSilentRefresh();
    });
  }

  private setupErrorHandling(): void {
    // Set up OAuth event subscription (only once)
    if (!this.errorHandlerSetup) {
      this.oauthService.events.subscribe((event) => {
        if (event instanceof OAuthErrorEvent) {
          console.error('OAuth error event:', event);
          this.handleOAuthError(event);
        } else {
          console.log('OAuth event:', event);
        }
      });
      this.errorHandlerSetup = true;
    }
  }

  private handleOAuthError(event: OAuthErrorEvent): void {
    // Handle runtime OAuth errors (after initialization)
    if (this.isRecoverableAuthError(event.reason)) {
      console.warn(
        'Runtime recoverable auth error detected, clearing state...'
      );
      this.clearAuthStateAndRetry();
    } else {
      console.error('Non-recoverable OAuth error:', event.reason);
      // We could also consider taking other actions here
    }
  }

  private isRecoverableAuthError(error: any): boolean {
    const recoverableErrors = [
      'invalid_nonce_in_state',
      'invalid_state',
      'nonce_mismatch',
      'state_mismatch',
      'invalid_request',
    ];

    const errorString =
      error?.type ||
      error?.reason ||
      error?.error ||
      error?.message ||
      error?.toString() ||
      '';
    return recoverableErrors.some((err) =>
      errorString.toLowerCase().includes(err.toLowerCase())
    );
  }

  private clearAuthStateAndRetry(resolve?: Function, reject?: Function): void {
    try {
      // Clear OAuth state
      this.oauthService.logOut(true); // true = clear storage

      // Clear additional OAuth-related items that might be causing issues
      this.clearOAuthStorage();

      // Small delay to ensure cleanup completes
      setTimeout(() => {
        // Whether called during initialization or runtime, if we need to recover from
        // an auth error (like invalid nonce), we must start a fresh login flow.
        // Retrying initialization with the same URL params would just fail again.
        if (resolve) {
          console.log(
            'Redirecting to login to recover from initialization error...'
          );
          resolve(); // Resolve the promise so the app doesn't crash while redirecting
        }
        this.initiateLogin();
      }, 100);
    } catch (error) {
      console.error('Error during auth state cleanup:', error);
      if (reject) {
        reject(error);
      }
    }
  }

  private clearOAuthStorage(): void {
    // Clear common OAuth storage keys
    const keysToRemove = [
      'nonce',
      'PKCE_verifier',
      'oauth-state',
      'access_token',
      'id_token',
      'expires_at',
      'id_token_claims_obj',
      'id_token_expires_at',
      'id_token_stored_at',
      'access_token_stored_at',
      'granted_scopes',
      'session_state',
    ];

    // remove from both to be thorough, should only need to clear localStorage though
    keysToRemove.forEach((key) => {
      this.localStorageService.removeKey(key);
      sessionStorage.removeItem(key);
    });
  }

  private initiateLogin(): void {
    const state = window.location.pathname + window.location.search;
    this.oauthService.initCodeFlow(state);
  }

  public hasInitializedValue(): boolean {
    return this.init$.getValue();
  }

  hasInitialized(): Observable<boolean> {
    return this.init$.asObservable();
  }

  public isAuthStateValid(): boolean {
    try {
      return (
        this.oauthService.hasValidAccessToken() &&
        this.oauthService.getIdentityClaims() !== null
      );
    } catch (error) {
      console.warn('Auth state validation failed:', error);
      return false;
    }
  }

  // Initiate the login process
  public login() {
    this.oauthService.initLoginFlow();
  }

  // TODO: we could potentially implement some other component that would enable
  // manual recovery of broken auth states.
  public recoverFromAuthError(): void {
    console.log('Manually triggering auth recovery...');
    this.clearAuthStateAndRetry();
  }

  // Log out and clear all session information
  public logout(): void {
    this.oauthService.logOut();
  }

  public hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // Check if the user is authenticated
  public isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // Get the token if needed for manual HTTP requests
  public getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  public getRefreshToken(): string {
    return this.oauthService.getRefreshToken();
  }

  public getIdentityClaims() {
    return this.oauthService.getIdentityClaims();
  }

  public refreshToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      navigator.locks.request('refreshTokenLock', async () => {
        try {
          const response = await this.oauthService.refreshToken();

          // Optionally validate response
          if (!response || !response.access_token) {
            return reject(
              new Error('Refresh response did not include an access token')
            );
          }

          const newToken = this.getAccessToken(); // Make sure this now returns updated token
          if (!newToken) {
            return reject(new Error('No access token found after refresh'));
          }

          console.log('Token refreshed successfully.');
          resolve(newToken);
        } catch (error) {
          console.error('Error refreshing token:', error);
          reject(error);
        }
      });
    });
  }

  public getAccessTokenExpiration(): number {
    return this.oauthService.getAccessTokenExpiration();
  }

  public getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  public loadUserProfile(): Promise<object> {
    return this.oauthService.loadUserProfile();
  }

  public getUsername() {
    return this.user;
  }

  public getUserId() {
    return this.sub;
  }

  public getState() {
    return this.oauthService.state;
  }

  private getUserSegmentData(): Observable<any> {
    return this.httpClient.get(
      environment.apiDomain + '/api/v1/UserSegmentData'
    );
  }

  private initializePendo(userId: string) {
    if (!environment.enablePendo) {
      return;
    }

    this.getUserSegmentData().subscribe({
      next: (response) => {
        const { data } = response as any;
        const {
          username: email,
          firstName,
          lastName,
          organizationId: id,
          organizationName: accountName,
        } = data;

        const pendoOpts = {
          visitor: {
            id: email,
            email,
            firstName,
            lastName,
            accountId: userId,
            organizationId: id,
            organizationName: accountName,
          },
          account: { id: accountName, organizationId: id, accountName },
        };
        pendo.initialize(pendoOpts);
      },
      error: (err) => console.error('Error fetching user segment data:', err),
    });
  }
}
